/**
 * Resolution Timelines settings tab (ADO #511 — "SLA" in code, never in copy).
 *
 * Only four of the stored `SlaConfig` values are read by anything today: the Red Dot
 * window (red-dot-sla.util.ts), the green/yellow bands (task-escalation.service.ts) and
 * hold auto-release (clinical-hold.service.ts). The other seven are persisted but no
 * service reads them, so they render read-only under "Not in effect yet" rather than
 * inviting admins to change clinical behaviour that does not exist. Their stored values
 * are left untouched and still round-trip on save. When one gets built, move its field
 * up into the live cards and drop `inactive`.
 */

import { useEffect, useState } from 'react';
import {
  DEFAULT_RED_DOT_SLA_DAYS,
  DEFAULT_SLA_GREEN_THRESHOLD,
  DEFAULT_SLA_YELLOW_THRESHOLD,
} from '@upskill/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import organizationService, { SlaConfig, SlaTier } from '../../services/api/organizationService';
import { useAuthStore } from '../../stores/authStore';

// ============================================================================
// Styled Components
// ============================================================================

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const ContentCard = styled(Card)``;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[5]};
  padding-bottom: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[3]} 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  }
`;

const FieldInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
  flex: 1 1 240px;
  overflow-wrap: anywhere;
`;

const FieldLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldDescription = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CardIntro = styled.p`
  margin: -${({ theme }) => theme.spacing[2]} 0 ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LabelRow = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const NumberInput = styled.input`
  width: 96px;
  height: 40px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.backgrounds.light};
    color: ${({ theme }) => theme.colors.text.secondary};
    cursor: not-allowed;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
  }
`;

const SelectInput = styled.select`
  height: 40px;
  min-width: 160px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;

  &:disabled {
    background: ${({ theme }) => theme.colors.backgrounds.light};
    color: ${({ theme }) => theme.colors.text.secondary};
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

const ToggleTrack = styled.button<{ $on: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: none;
  background: ${({ theme, $on }) =>
    $on ? theme.colors.primary.base : theme.colors.borders.medium};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.base};
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const ToggleThumb = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ $on }) => ($on ? '22px' : '3px')};
  width: 18px;
  height: 18px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.text.white};
  transition: left ${({ theme }) => theme.transitions.base};
`;

const SaveRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const SuccessMsg = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.success.text};
`;

const ErrorMsg = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
`;

const SkeletonGroup = styled.div`
  margin-top: ${({ theme }) => theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SkeletonBar = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${({ $width }) => $width ?? '100%'};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

// ============================================================================
// Sub-components
// ============================================================================

interface FieldTextProps {
  label: string;
  description?: string;
  /** Stored but not read by any service yet — shown read-only, with a badge. */
  inactive?: boolean;
}

const FieldText = ({ label, description, inactive }: FieldTextProps) => (
  <FieldInfo>
    <LabelRow>
      <FieldLabel>{label}</FieldLabel>
      {inactive && <Badge variant="neutral">Not in effect yet</Badge>}
    </LabelRow>
    {description && <FieldDescription>{description}</FieldDescription>}
  </FieldInfo>
);

interface ToggleSwitchProps extends FieldTextProps {
  value: boolean;
  onChange: (val: boolean) => void;
}

const ToggleSwitch = ({ label, description, inactive, value, onChange }: ToggleSwitchProps) => (
  <FormRow>
    <FieldText label={label} description={description} inactive={inactive} />
    <ToggleTrack
      type="button"
      $on={value}
      disabled={inactive}
      onClick={() => onChange(!value)}
      aria-pressed={value}
      aria-label={label}
    >
      <ToggleThumb $on={value} />
    </ToggleTrack>
  </FormRow>
);

interface NumberFieldProps extends FieldTextProps {
  value: number;
  min: number;
  max: number;
  onChange: (val: number) => void;
}

const NumberField = ({ label, description, inactive, value, min, max, onChange }: NumberFieldProps) => (
  <FormRow>
    <FieldText label={label} description={description} inactive={inactive} />
    <NumberInput
      type="number"
      aria-label={label}
      disabled={inactive}
      min={min}
      max={max}
      value={value}
      onChange={(e) => {
        const parsed = parseInt(e.target.value, 10);
        if (!isNaN(parsed)) {
          onChange(Math.min(max, Math.max(min, parsed)));
        }
      }}
    />
  </FormRow>
);

interface SelectFieldProps extends FieldTextProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (val: string) => void;
}

const SelectField = ({ label, description, inactive, value, options, onChange }: SelectFieldProps) => (
  <FormRow>
    <FieldText label={label} description={description} inactive={inactive} />
    <SelectInput
      aria-label={label}
      value={value}
      disabled={inactive}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </SelectInput>
  </FormRow>
);

// ============================================================================
// Constants
// ============================================================================

const TIER_OPTIONS = [
  { value: 'NORMAL', label: 'Normal' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'ESCALATED', label: 'Escalated' },
];

/**
 * Placeholder used until the org's own config loads. Every value here MUST match the
 * Prisma `SlaConfig` defaults (apps/api/prisma/schema.prisma) — a mismatch shows a value
 * the org has never chosen. Exported so the tab's tests can pin it against those
 * defaults; the three SLA numbers the API also falls back to come from @upskill/shared.
 */
export const DEFAULT_CONFIG: SlaConfig = {
  greenThreshold: DEFAULT_SLA_GREEN_THRESHOLD,
  yellowThreshold: DEFAULT_SLA_YELLOW_THRESHOLD,
  forcedAckEnabled: false,
  forcedAckTier: 'WARNING',
  emailNotificationTier: 'ESCALATED',
  holdAutoReleaseHours: 48,
  caseloadReviewDueHours: 48,
  repeatedBreachThreshold: 3,
  repeatedBreachWindowDays: 90,
  useBusinessDays: false,
  redDotSlaDays: DEFAULT_RED_DOT_SLA_DAYS,
  maxTasksPerUser: 15,
};

// ============================================================================
// Main Component
// ============================================================================

export const SlaSettingsTab = () => {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  const canEdit = hasPermission('org:settings:manage');
  const [form, setForm] = useState<SlaConfig>(DEFAULT_CONFIG);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ['sla-config'],
    queryFn: () => organizationService.getSlaConfig(),
  });

  useEffect(() => {
    if (config) {
      setForm(config);
    }
  }, [config]);

  const mutation = useMutation({
    mutationFn: (data: Partial<SlaConfig>) => organizationService.updateSlaConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sla-config'] });
      setSaved(true);
      setSaveError(null);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: () => {
      setSaveError('Failed to save resolution timeline settings. Please try again.');
    },
  });

  const update = <K extends keyof SlaConfig>(key: K, value: SlaConfig[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
    setSaveError(null);
  };

  if (isLoading) {
    return (
      <Section>
        <ContentCard padding="lg">
          <SkeletonBar $width="200px" />
          <SkeletonGroup>
            <SkeletonBar />
            <SkeletonBar />
            <SkeletonBar $width="70%" />
          </SkeletonGroup>
        </ContentCard>
      </Section>
    );
  }

  return (
    <Section>
      {/* Red Dots */}
      <ContentCard padding="lg">
        <CardTitle>Red Dots</CardTitle>
        <NumberField
          label="Red Dot Resolution Timeline (days)"
          description="Calendar days a Red Dot has to be resolved, measured from when it is assigned (1–365). Weekends count. Platform default is 14. Changes apply to newly assigned Red Dots only — Red Dots already assigned keep the deadline they were given."
          value={form.redDotSlaDays}
          min={1}
          max={365}
          onChange={(v) => update('redDotSlaDays', v)}
        />
      </ContentCard>

      {/* Status colours */}
      <ContentCard padding="lg">
        <CardTitle>Status Colours</CardTitle>
        <CardIntro>
          A task's due-date pill is coloured by how much of its resolution timeline is still left.
          Past the deadline it shows as overdue.
        </CardIntro>
        <NumberField
          label="Green Threshold (%)"
          description="Green while more than this percentage of the timeline is left (1–100). Default 50."
          value={form.greenThreshold}
          min={1}
          max={100}
          onChange={(v) => update('greenThreshold', v)}
        />
        <NumberField
          label="Yellow Threshold (%)"
          description="Yellow while more than this percentage is left, but no more than the green threshold. At or below it, red (1–100). Default 20."
          value={form.yellowThreshold}
          min={1}
          max={100}
          onChange={(v) => update('yellowThreshold', v)}
        />
      </ContentCard>

      {/* Clinical holds */}
      <ContentCard padding="lg">
        <CardTitle>Clinical Holds</CardTitle>
        <NumberField
          label="Hold Auto-Release (hours)"
          description="Hours before a clinical hold on a task is released automatically (1–168). Applies to holds placed after you save. Default 48."
          value={form.holdAutoReleaseHours}
          min={1}
          max={168}
          onChange={(v) => update('holdAutoReleaseHours', v)}
        />
      </ContentCard>

      {/* Stored but not yet read by any service — see the file header. */}
      <ContentCard padding="lg">
        <CardTitle>Not in effect yet</CardTitle>
        <CardIntro>
          These settings are saved for your organization, but the platform does not act on them
          yet. They are read-only until each one is built, so changing them cannot affect any
          deadline, task or notification today.
        </CardIntro>
        <ToggleSwitch
          inactive
          label="Forced Acknowledgement"
          description="Require staff to acknowledge overdue tasks before continuing."
          value={form.forcedAckEnabled}
          onChange={(v) => update('forcedAckEnabled', v)}
        />
        <SelectField
          inactive
          label="Forced Acknowledgement Tier"
          description="Lowest escalation tier that would require acknowledgement."
          value={form.forcedAckTier}
          options={TIER_OPTIONS}
          onChange={(v) => update('forcedAckTier', v as SlaTier)}
        />
        <SelectField
          inactive
          label="Email Notification Tier"
          description="Lowest escalation tier that would send an email."
          value={form.emailNotificationTier}
          options={TIER_OPTIONS}
          onChange={(v) => update('emailNotificationTier', v as SlaTier)}
        />
        <NumberField
          inactive
          label="Caseload Review Due (hours)"
          description="Hours before a caseload review task would count as overdue."
          value={form.caseloadReviewDueHours}
          min={1}
          max={168}
          onChange={(v) => update('caseloadReviewDueHours', v)}
        />
        <NumberField
          inactive
          label="Repeated Breach Threshold"
          description="Missed deadlines within the window before a repeat escalation."
          value={form.repeatedBreachThreshold}
          min={1}
          max={20}
          onChange={(v) => update('repeatedBreachThreshold', v)}
        />
        <NumberField
          inactive
          label="Repeated Breach Window (days)"
          description="Rolling window for counting repeated missed deadlines."
          value={form.repeatedBreachWindowDays}
          min={1}
          max={365}
          onChange={(v) => update('repeatedBreachWindowDays', v)}
        />
        <ToggleSwitch
          inactive
          label="Use Business Days"
          description="Count only weekdays toward resolution timelines. Red Dot deadlines always use calendar days today."
          value={form.useBusinessDays}
          onChange={(v) => update('useBusinessDays', v)}
        />
        <NumberField
          inactive
          label="Max Tasks Per User"
          description="Most open tasks one person could be assigned. Not enforced — assignment is never refused."
          value={form.maxTasksPerUser}
          min={1}
          max={500}
          onChange={(v) => update('maxTasksPerUser', v)}
        />
      </ContentCard>

      {/* Save */}
      <SaveRow>
        {form.yellowThreshold >= form.greenThreshold && (
          <ErrorMsg>Yellow threshold must be less than green threshold.</ErrorMsg>
        )}
        {saved && <SuccessMsg>Resolution timeline settings saved.</SuccessMsg>}
        {saveError && <ErrorMsg>{saveError}</ErrorMsg>}
        {canEdit && (
          <Button
            variant="primary"
            onClick={() => mutation.mutate(form)}
            isLoading={mutation.isPending}
            disabled={mutation.isPending || form.yellowThreshold >= form.greenThreshold}
          >
            Save Settings
          </Button>
        )}
      </SaveRow>
    </Section>
  );
};

export default SlaSettingsTab;
